import { Link, usePage } from "@inertiajs/react"
import { Briefcase, House, Plus, Search, Settings } from "lucide-react"
import { useEffect, useState } from "react"

import imperativeIcon from "@/assets/imperative_icon.svg"
import { ClientSearchDialog } from "@/components/client-search-dialog"
import { InboxNav } from "@/components/inbox-popover"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { Button } from "@/components/ui/button"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { askPath, homePath, jobsPath, settingsIdentityPath } from "@/routes"
import type { NavItem, Partner } from "@/types"

const newSessionItem: NavItem = {
  title: "New session",
  href: askPath(),
  icon: Plus,
  neverActive: true,
}

// IMP-520: the nav is the workspace — Home (the feed) and Jobs — replacing
// the per-agent Jobs group. Per-job management lives on the Jobs page.
const workspaceNavItems: NavItem[] = [
  {
    title: "Home",
    href: homePath(),
    icon: House,
    exactMatch: true,
  },
  {
    title: "Jobs",
    href: jobsPath(),
    icon: Briefcase,
  },
]

export function AppSidebar() {
  const page = usePage()
  const sharedProps = page.props as {
    has_custom_agents?: boolean
    partner?: Partner | null
  }
  // IMP-522: the search dialog, from the nav item or ⌘K.
  const [searchOpen, setSearchOpen] = useState(false)
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setSearchOpen((current) => !current)
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [])
  // IMP-256: the "New session" item only appears when the account has a custom
  // agent to chat with.
  const topNavItems = sharedProps.has_custom_agents ? [newSessionItem] : []
  const partner = sharedProps.partner ?? null
  const displayName = partner?.name ?? "Imperative"
  const logoSrc = partner?.logo_url ?? imperativeIcon
  const logoAlt = partner?.logo_url ? partner.name : "Imperative"

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex items-center gap-3 p-2 group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0">
              <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white shadow-sm ring-1 ring-slate-900/5">
                <img
                  src={logoSrc}
                  alt={logoAlt}
                  className="size-9 rounded-full object-cover"
                />
              </div>
              <div className="flex flex-col leading-none group-data-[collapsible=icon]:hidden">
                <span className="truncate text-sm font-medium">
                  {displayName}
                </span>
              </div>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* IMP-522/519: Search then Inbox above the workspace — actions and
            queues, not places. */}
        <NavMain items={topNavItems}>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip={{ children: "Search" }}
              className="cursor-pointer"
              onClick={() => setSearchOpen(true)}
            >
              <Search />
              <span>Search</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <InboxNav />
        </NavMain>
        <NavMain items={workspaceNavItems} label="Workspace" className="mt-4" />
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
        <div className="bg-sidebar-border -mx-2 h-px" />
        <div className="flex items-center justify-between px-1 group-data-[collapsible=icon]:justify-center">
          {/* IMP-459: settings opens the assistant's Identity page; hidden
              when the sidebar is collapsed. */}
          <Button
            variant="ghost"
            size="icon"
            className="size-7 group-data-[collapsible=icon]:hidden"
            asChild
          >
            <Link href={settingsIdentityPath()} aria-label="Settings" prefetch>
              <Settings />
            </Link>
          </Button>
          {/* Collapsing to the icon rail is desktop-only; on mobile the
              sidebar is a sheet, dismissed by tapping outside it. */}
          <SidebarTrigger className="hidden md:inline-flex" />
        </div>
      </SidebarFooter>

      <ClientSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </Sidebar>
  )
}
